/* eslint-disable default-param-last */
import { GlobalState } from '@leverege/ui-redux';
import { Constants, InterfaceReducer, InterfaceActions, Can } from '@leverege/api-redux';
import { ValuesCache } from '@leverege/value-cache';
import ItemDelegate from "./ItemDelegate.js"; // eslint-disable-line import/no-cycle
import Util from "../../routes/Util.js";
import { push } from "../../routes/History.js";
import AbstractGroupDelegate from "../../dataSource/AbstractGroupDelegate.js";
import { ImagineComparatorConverterFactory, ImagineFilterConverterFactory, ImagineSortConverterFactory } from "./Factory.js";
import HistoryGroupDelegate from "./HistoryGroupDelegate.js";
import RelationshipUrlMutatorFactory from "./urlMutators/RelationshipUrlMutatorFactory.js";
import { DelegateActions } from "./constants/index.js";
import LocalCanResolver from "./LocalCanResolver.js";
export default class GroupDelegate extends AbstractGroupDelegate {
  static TYPE = 'imagine.GroupDelegate';
  constructor(options) {
    super(options);
    const {
      match,
      relationship,
      filter,
      queryName,
      ...rest
    } = options;
    this.type = GroupDelegate.TYPE;
    this.match = match;
    this.relationship = relationship.isResolved ? relationship : relationship.createResolved(match);
    this.systemId = relationship.systemId;
    this.id = relationship.id;
    this.filter = filter;
    this.queryName = queryName;
    this.options = rest;
    this.isFilteredCache = new ValuesCache();
    this.localCanResolver = new LocalCanResolver({
      relationship,
      match
    });
    Object.defineProperties(this, {
      historyDelegate: {
        get: () => {
          if (!this.hDelegate) {
            this.hDelegate = new HistoryGroupDelegate(options, this);
          }
          return this.hDelegate;
        }
      }
    });
    if (relationship.match == null) {
      throw new Error('GroupDelegate requires a resolved Relationship');
    }
  }
  can(action) {
    return new Can(this.getInterfaceUrl(action), !this.relationship.parent, true);
  }
  getGroup() {
    return this;
  }

  /**
   * Returns the url for the interface for this action, resolved
   * to reference the ids supplied by the patch object. This will
   * include the systemId.
   */
  getInterfaceUrl(action, url = this.relationship.ref) {
    let nUrl = url;
    if (action) {
      const mutators = this.relationship.interfaceUrlMutators?.[action]?.map(mutator => RelationshipUrlMutatorFactory.create(mutator)) || [];
      for (let i = 0; i < mutators.length; i++) {
        nUrl = mutators[i]?.(nUrl);
      }
    }
    return nUrl;
  }
  getOverrides(action) {
    return this.relationship.interfaceOverrides?.[action];
  }

  /**
   * The inteface url to the parent, which will be an item url
   */
  getParentInterfaceUrl() {
    const parent = this.relationship.parent;
    return parent ? parent.createItem(this.match).objRefUrl : null;
  }

  /**
   * Returns the url for this group action object
   */
  getUrl(forInterfaceUrl = false, action) {
    if (forInterfaceUrl) {
      return this.getInterfaceUrl(action);
    }
    return Util.resolve(this.relationship.urlPath, this.match);
  }

  /**
   * Returns the parent's action object. This will return an ItemAction pointed
   * at the parent holding this group
   */
  parent() {
    const parent = this.relationship.parent;
    if (parent == null) {
      return null;
    }

    // if parent is an item or an alias, use ItemDelegate
    if (parent.isItem || parent.isAlias) {
      return new ItemDelegate({
        relationship: parent,
        // .createResolved( this.match ), 
        match: this.match,
        ...this.options
      });
    }
    return new GroupDelegate({
      relationship: parent,
      // .createResolved( this.match ), 
      match: this.match,
      ...this.options
    });
  }

  /**
   * Creates a item action for the id in the group.
   * @deprecated use item( id )
   */
  itemActions(id) {
    return this.item(id);
  }

  /**
   * Creates a item action for the id in the group.
   */
  item(id) {
    const iRel = this.relationship.createItem(this.match, id);
    return new ItemDelegate({
      match: iRel.match,
      relationship: iRel,
      id
    });
  }
  itemRef(id) {
    return this.relationship.createItem(this.relationship.match, id).objRef;
  }

  /**
   * Returns a redux action that will call api to create an item as a child of the current path
   * @param {object} obj item to be created
   * @param {object} options api options
   * @returns {function} redux thunk function 
   */
  create(obj, options) {
    return InterfaceActions.create(this.getInterfaceUrl(DelegateActions.CREATE), obj, this.id, Constants.INTERFACE_CREATE_ADD, {
      resolveOnWrite: true,
      refresh: 'wait_for',
      ...options
    }, {
      ...this.getOverrides(DelegateActions.CREATE),
      /* this ensures that if creation and search occur at different url's
         the search path gets updated with the new item
        */
      createEvent: evt => {
        return {
          ...evt,
          path: this.getInterfaceUrl(DelegateActions.SEARCH)
        };
      }
    });
  }

  /**
   * Returns a redux action that will ask the api if you can create an item at the current path
   * @param {object} query  query object to send to the api
   * @param {object} body the data of the object to create
   */
  requestCanCreate(query, body) {
    return this.can(DelegateActions.CREATE).create(query, body);
  }

  /**
   * Like create, but it will use the id to add an already existing device. By default,
   * force is set true to reparent the object
   */
  add(id, options) {
    const {
      force = true,
      ...opts
    } = options || {};
    return InterfaceActions.create(this.getInterfaceUrl(DelegateActions.CREATE), {
      id,
      force
    }, this.id, Constants.INTERFACE_CREATE_ADD, opts, this.getOverrides(DelegateActions.CREATE));
  }
  getDeviceByNetworkAlias(networkId, externalId, identifier = 'deviceId') {
    const url = this.getInterfaceUrl();
    return InterfaceActions.getDeviceByNetworkAlias(url, networkId, identifier, externalId);
  }

  /**
   * Create a dispatch action for a request of a list of objects
   */
  list(options) {
    let opts = options;
    let queryName = this.queryName;
    if (opts == null) {
      opts = {};
      if (this.filter) {
        opts.filter = this.filter;
      }
      if (this.options.ttl) {
        opts.ttl = this.options.ttl;
      }
    } else if (opts.queryName) {
      queryName = opts.queryName;
      opts = {
        ...opts
      };
      delete opts.queryName;
    }
    const url = this.getInterfaceUrl(DelegateActions.SEARCH);
    return InterfaceActions.list(url, null, Constants.INTERFACE_LIST, opts, queryName, this.getOverrides(DelegateActions.SEARCH));
  }
  canConvertCondition(cnd, cxt) {
    return ImagineFilterConverterFactory.get(cnd) != null;
  }
  canConvertComparator(cmp, cxt) {
    return ImagineComparatorConverterFactory.get(cmp) != null;
  }
  convertToSort(attrSortModel, cxt) {
    return ImagineSortConverterFactory.create(attrSortModel, {
      ...cxt,
      sortConverterFactory: ImagineSortConverterFactory
    });
  }
  isAttributeSorted = (attribute, sort) => {
    if (!attribute || !sort) {
      return false;
    }
    return sort.fields.some(field => {
      return field.attrName === attribute?.name;
    });
  };
  convertToFilter(cnd, cxt) {
    const filter = ImagineFilterConverterFactory.create(cnd, {
      ...cxt,
      filterConverterFactory: ImagineFilterConverterFactory,
      comparatorConverterFactory: ImagineComparatorConverterFactory
    });
    return filter;
  }
  isAttributeFiltered = (attribute, filter) => {
    if (!attribute || !filter) {
      return false;
    }
    return this.isFilteredCache.calc(`${attribute.id}-isFiltered`, () => {
      return this.doConditionsContainAttribute(filter.rootCondition.conditions, attribute);
    }, attribute, filter);
  };
  doConditionsContainAttribute(conditions, attribute) {
    return conditions.some(condition => {
      if (condition.conditions) {
        return this.doConditionsContainAttribute(condition.conditions, attribute);
      }
      if (condition.attrName !== attribute.name) {
        return false;
      }
      const filter = this.convertToFilter(condition, {
        objectType: attribute.objectType,
        filterConverterFactory: ImagineFilterConverterFactory,
        comparatorConverterFactory: ImagineComparatorConverterFactory
      });
      return filter != null;
    });
  }
  searchWithCondition({
    condition,
    sortModel,
    ...rest
  }, options, cxt) {
    const filter = this.convertToFilter(condition, cxt);
    const sort = this.convertToSort(sortModel, cxt);
    return this.search({
      filter,
      sort,
      ...rest
    }, options);
  }
  search(obj, options) {
    let search = obj;
    let queryName = this.queryName;
    if (search == null) {
      search = {};
      if (this.search) {
        search.filter = this.filter;
      }
      if (this.options.ttl) {
        search.ttl = this.options.ttl;
      }
    } else if (search.queryName) {
      queryName = search.queryName;
      search = {
        ...search
      };
      delete search.queryName;
    }
    const url = this.getInterfaceUrl(DelegateActions.SEARCH);
    const overrides = this.getOverrides(DelegateActions.SEARCH);
    return InterfaceActions.search(url, search, null, Constants.INTERFACE_LIST, options, queryName, overrides);
  }

  /**
   * Returns a redux action that will ask the api if you can read the item at the current path
   * @param {object} query  query object to send to the api
   */
  requestCanList(query) {
    return this.can(DelegateActions.SEARCH).list(query);
  }

  /**
   * Returns a redux action that will call api to update a child of the current path
   * @param {string} id id of the item to be updated
   * @param {object} data values of the item to be changed
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  update(id, data, options) {
    return this.item(id).update(data, {
      resolveOnWrite: true,
      refresh: 'wait_for',
      ...options
    });
  }

  /**
   * Returns a redux action that will ask the api if you can update the item at the current path
   * * @param {string} id id of the item to ask api about updating
   * @param {object} query  query object to send to the api
   * @param {object} body the data of the update
   */
  requestCanUpdate(id, query, body) {
    return this.item(id).canUpdate(query, body);
  }

  /**
   * Returns a redux action that will call api to delete a child of the current path
   * @param {string} id id of the item to be deleted
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  delete(id, body, options, reducerOptions) {
    return this.item(id).delete(body, options, reducerOptions);
  }

  /**
   * Returns a redux action that will ask the api if you can delete the item at the current path
   * @param {string} id id of the item to be deleted
   * @param {object} query  query object to send to the api
   * @param {object} body body of the request to test
   * @param {string} body.deleteDevice optional, if set to 'force' will delete
   *                                   the device and not just the relationship to it at a deep path
   */
  requestCanDelete(id, query, body) {
    return this.item(id).canDelete(query, body);
  }

  /**
   * Returns a redux action that makes an api call to get the history of an object at the path
   * @param {object} query.query query params that the server will use 
   * @param {string} query.queryName query identifier 
   * @param {object} options api options
   * @returns {function} redux thunk function
   * @deprecated
   */
  history(query, options) {
    return this.historyDelegate.search(query, options);
  }

  /**
   * Returns a redux action that will ask the api if you can get history at the current path
   * @param {object} query  query object to send to the api
   * @deprecated
   */
  requestCanHistory(query) {
    return this.historyDelegate.requestCanList(query);
  }

  /**
   * Returns a redux action that makes an api call to get the events of an object at the path
   * @param {object} query.query query params that the server will use 
   * @param {string} query.queryName query identifier 
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  events(query, options) {
    const url = this.getInterfaceUrl();
    const pUrl = this.getParentInterfaceUrl();
    return InterfaceActions.getHistory(`${url}.events`, pUrl, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }

  /**
   * Returns a redux action that will ask the api if you can get events at the current path
   * @param {object} query  query object to send to the api
   */
  requestCanEvents(query) {
    return this.can().events(query);
  }
  isGotoAllowed() {
    return this.relationship?.attribute?.hasTag('itemScreen:none') !== true;
  }

  /**
   * Returns a dispatch object to go to the child's url. If id is undefined,
   * this will push a link to the group
   */
  goTo(id) {
    if (this.isGotoAllowed()) {
      const url = this.getUrl();
      if (id === undefined) {
        return push(url);
      }
      return push(`${url}/${id}`);
    }
    return {
      type: 'noop'
    };
  }

  // local access
  getStatus(state = GlobalState.get(), options) {
    const {
      queryName = this.queryName
    } = options || {};
    return InterfaceReducer.getStatus(state, this.getInterfaceUrl(DelegateActions.SEARCH), queryName);
  }
  getList(state = GlobalState.get(), options) {
    const opts = {
      ...options
    };
    delete opts.queryName;
    const {
      queryName = this.queryName
    } = options || {};
    return InterfaceReducer.getList(state, this.getInterfaceUrl(DelegateActions.SEARCH), opts, queryName);
  }

  /**
   * 
   * @param {*} state 
   * @param {*} queryName 
   * @param {*} options 
   * @returns
   * @deprecated
   */
  getHistory(state = GlobalState.get(), queryName, options) {
    return this.historyDelegate.getList(state, queryName, options);
  }
  isLoading(state = GlobalState.get(), options) {
    const {
      queryName = this.queryName
    } = options || {};
    return InterfaceReducer.isLoading(state, this.getInterfaceUrl(DelegateActions.SEARCH), queryName);
  }
  isDone(state = GlobalState.get(), options) {
    const {
      queryName = this.queryName
    } = options || {};
    return InterfaceReducer.isDone(state, this.getInterfaceUrl(DelegateActions.SEARCH), queryName);
  }
  // accessors

  /**
   * Accesses whether or not a user can create an item at this path in the api from state
   * @param {object} state redux state
   */
  canCreate(query, body, state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.CREATE).getCreate(state, body);
    const localCan = this.localCanResolver.genericCan(DelegateActions.CREATE, query, body, {
      allowed: can
    });
    if (can == null && localCan == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanCreate(query, body));
    }
    return can ?? localCan;
  }

  /**
   * Accesses whether or not a user can list the items at this path in the api from state
   * @param {object} state redux state
   */
  canList(state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can(DelegateActions.SEARCH).getList(state);
    if (can == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanList());
    }
    return can;
  }

  /**
   * Accesses whether or not a user can update an item at this path in the api from state
   * @param {object} state redux state
   * @param {string} id the id of the device to test against
   */
  canUpdate(id, state = GlobalState.get(), requestIfUnavailable = true) {
    if (id == null) {
      // eslint-disable-next-line no-console
      console.error('GroupAction.canUpdate() invoked with null id');
      return false;
    }
    return this.item(id).canUpdate(state, requestIfUnavailable);
  }

  /**
   * Accesses whether or not a user can delete an item at this path in the api from state
   * @param {string} id the id of the device to test against
   * @param {object} state redux state
   */
  canDelete(id, query, body, state = GlobalState.get(), requestIfUnavailable = true) {
    return this.item(id).canDelete(query, body, state, requestIfUnavailable);
  }

  /**
   * Accesses whether or not a user can get history at this path in the api from state
   * @param {object} state redux state
   * @param {string} id the id of the device to test against
   * @deprecated
   */
  canHistory(state = GlobalState.get(), requestIfUnavailable = true) {
    this.historyDelegate.canList(state, requestIfUnavailable);
  }

  /**
   * Accesses whether or not a user can get events at this path in the api from state
   * @param {object} state redux state
   * @param {string} id the id of the device to test against
   */
  canEvents(state = GlobalState.get(), requestIfUnavailable = true) {
    const can = this.can().getEvents(state);
    if (can == null && requestIfUnavailable) {
      GlobalState.dispatch(this.requestCanEvents());
    }
    return can;
  }
}